// Spades on screen (desktop + phone): the "Play Spades" prompt when you stand
// at a table, the lobby once you're seated, and the game itself — the trick,
// bids, scores, the bid picker and your hand. Spectators get the prompt only;
// the 3D table shows them the played cards and score. VR uses SpadesVRPanel.
// Mounted by GameOverlay (which owns the rules guide / leaderboard dialogs).

import React, { useEffect, useState } from 'react';
import { tableAction, handOf, turnDeadline, type TableState } from '../gameStore';
import { cardLabel, cardName, isRed, SUIT_GLYPH } from '../cardFaces';
import { legalPlays, SEAT_NAMES, partnerOf, type SpadesState, type Play } from '../../../../supabase/functions/games/spades.ts';
import { JoinPrompt, LobbyPanel, HelpButton, GameOverBlock, PanelError, TurnTimer, leaveTable } from '../kit/LobbyKit';
import type { OverlayProps } from '../types';
import './SpadesOverlay.css';

function MiniCard({ card, dim = false }: { card: string; dim?: boolean }) {
  return (
    <span className={`gx-sp-mini ${isRed(card) ? 'is-red' : ''} ${dim ? 'is-dim' : ''}`} aria-label={cardName(card)}>
      {cardLabel(card)}
    </span>
  );
}

const nameOf = (st: TableState, i: number) => st.seats[i]?.name ?? SEAT_NAMES[i];

function bidText(g: SpadesState, i: number) {
  const b = g.bids[i];
  return b === null ? '—' : b === 0 ? 'Nil' : String(b);
}

export function SpadesOverlay(props: OverlayProps) {
  const { def, pid, entry, table, seat, onHelp } = props;
  const [peek, setPeek] = useState(false);
  const [showLast, setShowLast] = useState(false);

  const st = table.state;
  const g = (st.g ?? null) as SpadesState | null;
  const hand = handOf(entry);
  const lastKey = g?.lastTrick ? `${g.handNo}:${g.played.length}` : '';
  useEffect(() => {
    if (!lastKey) return;
    setShowLast(true);
    const t = window.setTimeout(() => setShowLast(false), 2200);
    return () => window.clearTimeout(t);
  }, [lastKey]);

  // ---- Not seated: prompt ------------------------------------------------
  if (seat < 0) return <JoinPrompt {...props} />;

  const leave = () => leaveTable(pid);

  // ---- Seated, lobby ------------------------------------------------------
  if (st.phase === 'lobby') {
    return (
      <LobbyPanel {...props}
        seatLabel={(i) => `${SEAT_NAMES[i]}${i % 2 === seat % 2 ? ' · your team' : ''}`}
        footer="Friends can walk up and take the open seats. First team to 500 wins." />
    );
  }

  if (!g) return null;

  // ---- Seated, playing / over ---------------------------------------------
  const us = seat % 2, them = 1 - us;
  const myTurn = st.phase === 'playing' && g.turn === seat && (g.phase === 'bidding' || g.phase === 'playing');
  const legal = myTurn && g.phase === 'playing' ? new Set(legalPlays(g, hand)) : null;
  const rel = (i: number) => ['bottom', 'left', 'top', 'right'][(i - seat + 4) % 4];
  const trickPlays: Play[] = peek && g.lastTrick ? g.lastTrick.plays
    : g.trick.length ? g.trick : showLast && g.lastTrick ? g.lastTrick.plays : [];
  const trickWinner = (peek || (!g.trick.length && showLast)) && g.lastTrick ? g.lastTrick.winner : null;
  const turnName = g.turn === seat ? 'Your' : `${nameOf(st, g.turn)}'s`;

  let status: string;
  if (st.phase === 'over') status = g.winner === us ? 'You win the game!' : 'They win the game';
  else if (g.phase === 'hand_over' && g.lastHand) {
    const p = g.lastHand.points;
    status = `Hand over: us ${p[us] >= 0 ? '+' : ''}${p[us]}, them ${p[them] >= 0 ? '+' : ''}${p[them]}${g.lastHand.bagPenalty[us] ? ' (bag penalty!)' : ''} — next deal soon`;
  } else if (g.phase === 'bidding') status = myTurn ? 'Your bid' : `${turnName} bid…`;
  else status = myTurn ? (g.trick.length === 0 ? (g.spadesBroken ? 'Your lead' : 'Your lead — spades not broken yet') : 'Your turn') : `${turnName} turn`;

  const usBid = [us, us + 2].reduce((s, i) => s + (g.bids[i] ?? 0), 0);
  const usWon = g.tricksWon[us] + g.tricksWon[us + 2];
  const themBid = [them, them + 2].reduce((s, i) => s + (g.bids[i] ?? 0), 0);
  const themWon = g.tricksWon[them] + g.tricksWon[them + 2];

  return (
    <div className={`gx-sp gx-sp--panel gx-sp--game ${myTurn ? 'is-my-turn' : ''}`} role="region" aria-label="Spades game">
      <div className="gx-sp-head">
        <div className="gx-sp-score">
          <span className="gx-sp-team is-us">Us <b>{g.scores[us]}</b> <small>{usWon}/{usBid} tricks · {g.bags[us]} bags</small></span>
          <span className="gx-sp-team">Them <b>{g.scores[them]}</b> <small>{themWon}/{themBid} tricks · {g.bags[them]} bags</small></span>
        </div>
        <div className="gx-sp-head-actions">
          {g.lastTrick && (
            <button className={`gx-sp-btn gx-sp-btn--ghost ${peek ? 'is-on' : ''}`} onClick={() => setPeek((v) => !v)} aria-pressed={peek}>
              Last trick
            </button>
          )}
          <HelpButton def={def} onHelp={onHelp} compact />
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={leave}>Leave</button>
        </div>
      </div>

      <div className="gx-sp-table">
        {[0, 1, 2, 3].map((i) => {
          const play = trickPlays.find((p) => p.seat === i);
          const turn = st.phase === 'playing' && g.turn === i && (g.phase === 'bidding' || g.phase === 'playing');
          return (
            <div key={i} className={`gx-sp-spot gx-sp-spot--${rel(i)} ${turn ? 'is-turn' : ''} ${trickWinner === i ? 'is-winner' : ''}`}>
              <span className="gx-sp-spot-name">
                {i === seat ? 'You' : nameOf(st, i)}{i === partnerOf(seat) ? ' ★' : ''}
              </span>
              <span className="gx-sp-spot-bid">bid {bidText(g, i)} · {g.tricksWon[i]}</span>
              {play ? <MiniCard card={play.card} /> : <span className="gx-sp-mini is-empty" />}
            </div>
          );
        })}
        <div className="gx-sp-center">
          <span className={`gx-sp-status ${myTurn ? 'is-mine' : ''}`}>{peek ? 'Last trick' : status}</span>
          {myTurn && !peek && <TurnTimer deadline={turnDeadline(st)} />}
          {!g.spadesBroken && g.phase === 'playing' && <span className="gx-sp-note">{SUIT_GLYPH.S} not broken</span>}
        </div>
      </div>

      {st.phase === 'over' ? (
        <GameOverBlock {...props} summary={`${g.scores[us]} – ${g.scores[them]}`} />
      ) : myTurn && g.phase === 'bidding' ? (
        <div className="gx-sp-bids" role="group" aria-label="Choose your bid">
          {Array.from({ length: 14 }, (_, b) => (
            <button key={b} className={`gx-sp-bid ${b === 0 ? 'is-nil' : ''}`} disabled={entry.busy}
              onClick={() => void tableAction(pid, 'move', { move: { type: 'bid', bid: b } })}>
              {b === 0 ? 'Nil' : b}
            </button>
          ))}
        </div>
      ) : null}

      {hand.length > 0 && st.phase === 'playing' && (
        <div className="gx-sp-hand" role="group" aria-label="Your hand">
          {hand.map((c) => {
            const playable = !!legal?.has(c) && !entry.busy;
            return (
              <span key={c} className="gx-sp-slot">
                <button
                  className={`gx-sp-card ${isRed(c) ? 'is-red' : ''} ${legal && !playable ? 'is-dim' : ''} ${playable ? 'is-playable' : ''}`}
                  disabled={!playable}
                  aria-label={cardName(c)}
                  onClick={() => void tableAction(pid, 'move', { move: { type: 'play', card: c } })}
                >
                  <span className="gx-sp-card-rank">{cardLabel(c).slice(0, -1)}</span>
                  <span className="gx-sp-card-suit">{SUIT_GLYPH[c[1]]}</span>
                </button>
              </span>
            );
          })}
        </div>
      )}
      {st.note && <span className="gx-sp-note gx-sp-log">{st.note}</span>}
      <PanelError entry={entry} />
    </div>
  );
}

export default SpadesOverlay;
